import React, { memo, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { desktop, tablet } from 'services/ui';

import { Colors } from 'context';
import { PopupContainer } from 'molecules';
import { Button } from 'atoms';
import { PATHS } from 'router/config';
import { usePendingSurveysCount } from 'core/hooks';

/**
 * sessionStorage flag — the nag modal opens at most once per session PER USER
 * (spec 0011). Keyed by user id: logout does not clear sessionStorage, so a
 * bare key would silence the modal for the next student logging in on the
 * same tab (review 0011 finding).
 */
export const surveyNagSessionKey = (userId: string) => `tms_survey_nag_shown:${userId}`;

/**
 * Login-time survey reminder (spec 0011): opens once per session when the
 * student has pending surveys, offering «Пройти» (go to /surveys) or «Позже»
 * (dismiss — the persistent SurveyNagBanner keeps reminding). The count rides
 * the cached `me` query, which resolves async — hence the effect (not a
 * mount-time check) that fires the moment a positive count first arrives, and
 * the flag is written on open so later count changes never reopen it.
 */
export const SurveyNagModal = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { count, activeRole, userId } = usePendingSurveysCount();
  const [isShowing, setIsShowing] = useState(false);

  useEffect(() => {
    if (
      activeRole === 'Student' &&
      count > 0 &&
      userId &&
      !sessionStorage.getItem(surveyNagSessionKey(userId))
    ) {
      sessionStorage.setItem(surveyNagSessionKey(userId), '1');
      setIsShowing(true);
    }
  }, [activeRole, count, userId]);

  const onClose = useCallback(() => setIsShowing(false), []);

  const onTakeSurvey = useCallback(() => {
    setIsShowing(false);
    navigate(PATHS.surveys);
  }, [navigate]);

  return (
    <PopupContainer title={t('surveys.modal.title')} isShowing={isShowing} onClose={onClose}>
      <Wrapper>
        <Content>
          {count === 1 ? t('surveys.modal.text') : t('surveys.modal.textMany', { n: count })}
        </Content>

        <ButtonsWrapper>
          <LaterButton variant="text" onClick={onClose}>
            {t('surveys.modal.later')}
          </LaterButton>
          <TakeButton onClick={onTakeSurvey}>{t('surveys.modal.take')}</TakeButton>
        </ButtonsWrapper>
      </Wrapper>
    </PopupContainer>
  );
});

const Wrapper = styled.div``;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px 20px 40px;

  ${tablet} {
    padding: 32px 40px 64px;
  }

  ${desktop} {
    padding: 0 60px 64px;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 8px;
  background-color: ${Colors.bg_gray};
  padding: 20px 20px 32px;
  border-radius: 0 0 24px 24px;

  ${tablet} {
    flex-direction: row;
    justify-content: flex-end;
    gap: 16px;
    padding: 12px 40px;
  }

  ${desktop} {
    padding: 16px 60px;
  }
`;

const TakeButton = styled(Button)`
  ${tablet} {
    max-width: max-content;
  }
`;

const LaterButton = styled(TakeButton)``;
